// Treatment schedule rows. Changing the schedule closes the current row and starts a new one, so
// what was planned on any past date stays known (needed to spot missed sessions).
import { subDays } from 'date-fns';
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';
import { scheduleOn, toDateKey, type ScheduleException, type TreatmentSchedule } from '../lib/schedule';

export function useSchedules() {
  const schedules = useDbQuery<TreatmentSchedule>(
    'SELECT id, days_of_week, start_time, duration_min, location, modality, active_from, active_to FROM treatment_schedule WHERE deleted_at IS NULL ORDER BY active_from',
    [],
    ['treatment_schedule']
  );
  const exceptions = useDbQuery<ScheduleExceptionRow>(
    'SELECT id, date, kind, new_time, note FROM schedule_exceptions WHERE deleted_at IS NULL AND date >= ? ORDER BY date',
    [toDateKey(subDays(new Date(), 60))],
    ['schedule_exceptions']
  );
  const all = schedules.data ?? [];
  return {
    schedules: all,
    exceptions: exceptions.data ?? [],
    current: scheduleOn(all, toDateKey(new Date())),
    isLoading: schedules.isLoading || exceptions.isLoading
  };
}

export interface ScheduleExceptionRow extends ScheduleException {
  id: string;
  note: string | null;
}

export function addException(db: DbClient, values: Omit<ScheduleExceptionRow, 'id'>) {
  return db.insert('schedule_exceptions', values);
}

export function removeException(db: DbClient, id: string) {
  return db.softDelete('schedule_exceptions', id);
}

export type ScheduleInput = Pick<TreatmentSchedule, 'days_of_week' | 'start_time' | 'duration_min' | 'location' | 'modality'>;

export async function saveSchedule(db: DbClient, current: TreatmentSchedule | null, input: ScheduleInput) {
  const today = toDateKey(new Date());
  if (current && current.active_from === today) {
    return db.update('treatment_schedule', current.id, input);
  }
  if (current) await db.update('treatment_schedule', current.id, { active_to: toDateKey(subDays(new Date(), 1)) });
  return db.insert('treatment_schedule', { ...input, active_from: today, active_to: null });
}
